/**
 *
 * @param {HTMLElement} container
 */
import {isBlank, isEmpty, isNotEmpty, isNumber} from "../util/type-utils.js";
import {attachInputValueChangeEventListener} from "../util/event-utils.js";
import {find} from "@21torr/dune/dom/traverse.js";


const INITIAL_HIDDEN_CLASS = 'hidden-initial';
const INIT_CLASS = 'form-password-check-init';

export function initFormPasswordCheck(container) {

    if (isEmpty(container)) {
        return;
    }

    if (container.classList.contains(INIT_CLASS)) {
        return;
    }

    const inputElementId = container.dataset.for,
        inputElement = isBlank(inputElementId) ? null : document.getElementById(inputElementId);

    if (isEmpty(inputElement)) {
        return;
    }


    // build validator list
    const validators = buildValidatorList(container);


    if (isEmpty(validators)) {
        return;
    }

    attachInputValueChangeEventListener(inputElement, e => {
        let value = inputElement.value;

        validators.forEach(validator => {
            if (!!!validator.checkFunc(value)) {
                validator.el.classList.add("not-valid");
            } else {
                validator.el.classList.remove("not-valid");
            }

            if (container.classList.contains(INITIAL_HIDDEN_CLASS)) {
                container.classList.remove(INITIAL_HIDDEN_CLASS);
            }
        });


    }, 150, false);


    // disable multiple inits
    container.classList.add(INIT_CLASS)

}


/**
 * @typedef PasswordValidatorConfig{Object}
 * @property {HTMLElement} el
 * @property {Function} checkFunc
 */

/**
 *
 * @param {HTMLElement} container
 * @returns {PasswordValidatorConfig[]|null}
 *
 */
function buildValidatorList(container) {
    const validators = [];
    const entryElements = find('.form-check-entry', container);

    if (isEmpty(entryElements)) {
        return null;
    }

    entryElements.forEach(entry => {

        if (isNotEmpty(entry.dataset.minLength)) {
            validators.push({
                el: entry,
                checkFunc: (value) => {
                    return checkPasswordMinLength(entry.dataset.minLength, value)
                }
            });
            return;
        }

        if (isNotEmpty(entry.dataset.maxLength)) {
            validators.push({
                el: entry,
                checkFunc: (value) => {
                    return checkPasswordMaxLength(entry.dataset.maxLength, value)
                }
            });
            return;
        }

        if (isNotEmpty(entry.dataset.charRequired)) {
            validators.push({
                el: entry,
                checkFunc: (value) => {
                    return checkCharRequired(entry.dataset.charRequired, value)
                }
            });
            return;
        }

        if (isNotEmpty(entry.dataset.numberRequired)) {
            validators.push({
                el: entry,
                checkFunc: (value) => {
                    return checkNumberRequired(entry.dataset.numberRequired, value)
                }
            });
            return;
        }

        if (isNotEmpty(entry.dataset.blacklist)) {
            validators.push({
                el: entry,
                checkFunc: (value) => {
                    return checkBlacklist(entry.dataset.blacklist, value)
                }
            });
            return;
        }

    });


    return validators;
}

/**
 *
 * @param {String|Number} minLength
 * @param {String} value
 * @returns {boolean} returns true, if the passed value string is longer than the provided value for minlength or minlength less than 0
 */
function checkPasswordMinLength(minLength, value) {
    if (isBlank(minLength)) {
        return true;
    }

    value = value || ''; // prevent null exceptions

    try {
        let minLengthNum = Number((minLength));

        if (isBlank(value) && minLengthNum > 0) {
            return false;
        }


        return value.length >= minLengthNum;


    } catch (e) {
        console.warn("Caught error while validating min length")
        return true;
    }
}

/**
 *
 * @param {String|Number} maxLength
 * @param {String} value
 * @returns {boolean} returns true, if the passed value string is shorter than the provided value for maxlength or maxlength is empty
 */
function checkPasswordMaxLength(maxLength, value) {
    if (isBlank(maxLength)) {
        return true;
    }

    value = value || ''; // prevent null exceptions

    try {
        let maxLengthNum = Number((maxLength));

        if (!isBlank(value) && maxLengthNum <= 0) {
            return false;
        }


        return value.length <= maxLengthNum;


    } catch (e) {
        console.warn("Caught error while validating max length")
        return true;
    }
}

/**
 *
 * @param {String} required true|false
 * @param {String} value
 * @returns {boolean} returns true if the "required" value is false or the passed string is not blank and contains character(s)
 */
function checkCharRequired(required, value) {
    if (isEmpty(required)) {
        return true;
    }

    const requiredBool = required.toString().toLowerCase() === 'true';

    return !requiredBool || !isBlank(value) && /[abcdefghijklmnopqrstuvwxyz]+/i.test(value);
}


/**
 *
 * @param {String} required true|false
 * @param {String} value
 * @returns {boolean} returns true if the "required" value is false or the passed string is not blank and contains number(s)
 */
function checkNumberRequired(required, value) {
    if (isEmpty(required)) {
        return true;
    }

    const requiredBool = required.toString().toLowerCase() === 'true';

    return !requiredBool || !isBlank(value) && /\d+/i.test(value);
}

/**
 *
 * @param {String} blacklistString a comma-separated string of values that are now allowed in the passed value
 * @param {String} value
 * @returns {boolean} return true, if either the value or passed blacklist-string is empty or the value doesn't contain any of the blacklisted words
 */
function checkBlacklist(blacklistString, value) {
    if (isBlank(blacklistString) || isBlank(value)) {
        return true;
    }


    const blacklistValues = blacklistString.split(',').map(s => s.trim().toLowerCase());

    if (isEmpty(blacklistValues)) {
        return;
    }

    let valid = true;

    blacklistValues.forEach(blacklistedWord => {
        if (value.toLowerCase().includes(blacklistedWord)) {
            valid = false;
            return;
        }
    })


    return valid;

}